import * as React from "react"
import { Suspense } from "react"
import { Route, Routes, useLocation, Navigate, Outlet } from "react-router-dom"

import { DocumentTitle } from "@/components/app/document-title"
import { AuthenticatedShellOutlet } from "@/components/shell/authenticated-shell-outlet"
import { ShellLayout } from "@/components/shell/shell-layout"
import { useAuth } from "@/lib/auth"
import { ENABLE_WORKSPACE_CHATBOT } from "@/lib/feature-flags"

const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
)

const LazyContactPage = React.lazy(() => import("@/pages/contact-page").then((m) => ({ default: m.ContactPage })))
const LazyTermsPage = React.lazy(() => import("@/pages/legal/terms").then((m) => ({ default: m.TermsPage })))
const LazyPrivacyPage = React.lazy(() => import("@/pages/legal/privacy").then((m) => ({ default: m.PrivacyPage })))
const LazyCookiesPage = React.lazy(() => import("@/pages/legal/cookies").then((m) => ({ default: m.CookiesPage })))
const LazyBlogListPage = React.lazy(() => import("@/pages/blog-list-page").then((m) => ({ default: m.BlogListPage })))
const LazyBlogPostPage = React.lazy(() => import("@/pages/blog-post-page").then((m) => ({ default: m.BlogPostPage })))
const LazyBookDemoPage = React.lazy(() => import("@/pages/book-demo-page").then((m) => ({ default: m.BookDemoPage })))
const LazyAcceptInvitePage = React.lazy(() => import("@/pages/accept-invite-page").then((m) => ({ default: m.AcceptInvitePage })))
const LazyDashboardContentLayout = React.lazy(() =>
  import("@/components/shell/dashboard-content-layout").then((m) => ({ default: m.DashboardContentLayout }))
)
const LazyDashboardWorkspacesPage = React.lazy(() =>
  import("@/pages/dashboard-workspaces-page").then((m) => ({ default: m.DashboardWorkspacesPage }))
)
const LazyWorkspaceNewPage = React.lazy(() =>
  import("@/pages/workspace-new-page").then((m) => ({ default: m.WorkspaceNewPage }))
)
const LazyVisaExplorerPage = React.lazy(() =>
  import("@/pages/visa-explorer-page").then((m) => ({ default: m.VisaExplorerPage }))
)
const LazyDashboardVisaExplorerPage = React.lazy(() =>
  import("@/pages/dashboard-visa-explorer-page").then((m) => ({ default: m.DashboardVisaExplorerPage }))
)
const LazySettingsLayout = React.lazy(() =>
  import("@/pages/settings/settings").then((m) => ({ default: m.SettingsLayout }))
)
const LazySettingsIndexRedirect = React.lazy(() =>
  import("@/pages/settings/settings").then((m) => ({ default: m.SettingsIndexRedirect }))
)
const LazySettingsContent = React.lazy(() =>
  import("@/pages/settings/settings-content").then((m) => ({ default: m.SettingsContent }))
)

const LazyWorkspaceChromeLayout = React.lazy(() =>
  import("@/components/app/workspace-chrome-layout").then((m) => ({ default: m.WorkspaceChromeLayout }))
)

// Workspace tabs — lazy load all (split from main chunk)
const LazyWorkspaceOverviewPage = React.lazy(() =>
  import("../pages/workspace-overview-page").then((m) => ({ default: m.WorkspaceOverviewPage }))
)
const LazyWorkspaceTasksPage = React.lazy(() =>
  import("../pages/workspace-tasks-page").then((m) => ({ default: m.WorkspaceTasksPage }))
)
const LazyWorkspaceDocumentsPage = React.lazy(() =>
  import("../pages/workspace-documents-page").then((m) => ({ default: m.WorkspaceDocumentsPage }))
)
const LazyWorkspaceFinancesPage = React.lazy(() =>
  import("../pages/workspace-finances-page").then((m) => ({ default: m.WorkspaceFinancesPage }))
)
const LazyWorkspaceResourcesPage = React.lazy(() =>
  import("../pages/workspace-resources-page").then((m) => ({ default: m.WorkspaceResourcesPage }))
)
const LazyWorkspaceSettingsPage = React.lazy(() =>
  import("../pages/workspace-settings-page").then((m) => ({ default: m.WorkspaceSettingsPage }))
)
const LazyWorkspaceTimelinePage = React.lazy(() =>
  import("../pages/workspace-timeline-page").then((m) => ({ default: m.WorkspaceTimelinePage }))
)
const LazyWorkspaceChatbotSettingsPage = React.lazy(() =>
  import("../pages/workspace/chatbot-settings").then((m) => ({ default: m.WorkspaceChatbotSettingsPage }))
)

const LazyLoginPage = React.lazy(() => import("../pages/login").then((m) => ({ default: m.LoginPage })))
const LazySignUpPage = React.lazy(() => import("../pages/sign-up").then((m) => ({ default: m.SignUpPage })))
const LazyResetPasswordPage = React.lazy(() =>
  import("../pages/reset-password").then((m) => ({ default: m.ResetPasswordPage }))
)
const LazyUpdatePasswordPage = React.lazy(() =>
  import("../pages/update-password").then((m) => ({ default: m.UpdatePasswordPage }))
)
const LazyLandingPage = React.lazy(() =>
  import("../pages/landing-page").then((m) => ({ default: m.LandingPage }))
)
const LazyOnboardingPage = React.lazy(() =>
  import("../pages/onboarding-page").then((m) => ({ default: m.OnboardingPage }))
)
const LazyDashboardOverviewPage = React.lazy(() =>
  import("../pages/dashboard-overview-page").then((m) => ({ default: m.DashboardOverviewPage }))
)

const LazyAdminLayout = React.lazy(() => import("@/routes/admin/layout").then((m) => ({ default: m.AdminLayout })))
const LazyAdminHomePage = React.lazy(() => import("@/routes/admin/page").then((m) => ({ default: m.AdminHomePage })))
const LazyAdminVisaMetadataPage = React.lazy(() =>
  import("@/routes/admin/visa-metadata/page").then((m) => ({ default: m.AdminVisaMetadataPage }))
)
const LazyAdminVisaMetadataImportPage = React.lazy(() =>
  import("@/routes/admin/visa-metadata/import/page").then((m) => ({ default: m.AdminVisaMetadataImportPage }))
)
const LazyAdminVisaMetadataRouteDetailPage = React.lazy(() =>
  import("@/routes/admin/visa-metadata/[id]/page").then((m) => ({ default: m.AdminVisaMetadataRouteDetailPage }))
)
const LazyAdminAirlinesPage = React.lazy(() =>
  import("@/routes/admin/airlines/page").then((m) => ({ default: m.AdminAirlinesPage }))
)
const LazyAdminResourcesPage = React.lazy(() =>
  import("@/routes/admin/resources/page").then((m) => ({ default: m.AdminResourcesPage }))
)
const LazyAdminUsersPage = React.lazy(() =>
  import("@/routes/admin/users/page").then((m) => ({ default: m.AdminUsersPage }))
)
const LazyAdminHealthPage = React.lazy(() =>
  import("@/routes/admin/health/page").then((m) => ({ default: m.AdminHealthPage }))
)
const LazyAdminSettingsPage = React.lazy(() =>
  import("@/routes/admin/settings/page").then((m) => ({ default: m.AdminSettingsPage }))
)
const LazyNotFoundPage = React.lazy(() =>
  import("@/pages/not-found-page").then((m) => ({ default: m.NotFoundPage }))
)

function AuthLoading() {
  return (
    <div
      className="flex min-h-svh items-center justify-center bg-background p-4 text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      Loading…
    </div>
  )
}

function RequireAuthenticated() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return <AuthLoading />
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    )
  }

  return <Outlet />
}

function withLoader(el: React.ReactElement) {
  return <Suspense fallback={<PageLoader />}>{el}</Suspense>
}

function withWorkspaceLoader(el: React.ReactElement) {
  return (
    <Suspense fallback={<div className="p-8">Loading workspace...</div>}>{el}</Suspense>
  )
}

export function AppRouter() {
  return (
    <>
      <DocumentTitle />
      <Routes>
        <Route path="/login" element={withLoader(<LazyLoginPage />)} />
        <Route path="/signup" element={<Navigate to="/sign-up" replace />} />
        <Route path="/sign-up" element={withLoader(<LazySignUpPage />)} />
        <Route path="/verify-email" element={<Navigate to="/sign-up" replace />} />
        <Route path="/reset-password" element={withLoader(<LazyResetPasswordPage />)} />
        <Route path="/update-password" element={withLoader(<LazyUpdatePasswordPage />)} />
        <Route path="/visa-explorer" element={withLoader(<LazyVisaExplorerPage />)} />

        <Route element={<RequireAuthenticated />}>
          <Route element={<AuthenticatedShellOutlet />}>
            <Route path="/onboarding" element={withLoader(<LazyOnboardingPage />)} />
            <Route path="/workspace/new" element={<Navigate to="/dashboard/workspaces/new" replace />} />
            <Route path="/workspaces/new" element={<Navigate to="/dashboard/workspaces/new" replace />} />

            {/* All routes inside the persistent shell */}
            <Route element={<ShellLayout />}>
              <Route path="admin" element={withLoader(<LazyAdminLayout />)}>
                <Route index element={withLoader(<LazyAdminHomePage />)} />
                <Route path="health" element={withLoader(<LazyAdminHealthPage />)} />
                <Route path="settings" element={withLoader(<LazyAdminSettingsPage />)} />
                <Route path="visa-metadata" element={withLoader(<LazyAdminVisaMetadataPage />)} />
                <Route path="visa-metadata/import" element={withLoader(<LazyAdminVisaMetadataImportPage />)} />
                <Route path="visa-metadata/review" element={<Navigate to="/admin/visa-metadata?needs_review=1" replace />} />
                <Route path="visa-metadata/enrich" element={<Navigate to="/admin/visa-metadata" replace />} />
                <Route path="visa-metadata/:id" element={withLoader(<LazyAdminVisaMetadataRouteDetailPage />)} />
                <Route path="airlines" element={withLoader(<LazyAdminAirlinesPage />)} />
                <Route path="resources" element={withLoader(<LazyAdminResourcesPage />)} />
                <Route path="users" element={withLoader(<LazyAdminUsersPage />)} />
              </Route>
              {/* Dashboard & account routes — padded container */}
              <Route element={withLoader(<LazyDashboardContentLayout />)}>
                <Route path="/dashboard" element={withLoader(<LazyDashboardOverviewPage />)} />
                <Route path="/dashboard/workspaces" element={withLoader(<LazyDashboardWorkspacesPage />)}>
                  <Route path="new" element={withLoader(<LazyWorkspaceNewPage />)} />
                </Route>
                {/* Phase 2: legacy URL kept as redirect for bookmarks */}
                <Route path="/dashboard/templates" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/visa-explorer" element={withLoader(<LazyDashboardVisaExplorerPage />)} />
                <Route
                  path="/dashboard/resources"
                  element={<Navigate to="/dashboard/visa-explorer" replace />}
                />
                <Route path="/resources" element={<Navigate to="/dashboard/visa-explorer" replace />} />
                <Route path="/profile" element={<Navigate to="/settings/general" replace />} />
                <Route path="/settings" element={withLoader(<LazySettingsLayout />)}>
                  <Route index element={withLoader(<LazySettingsIndexRedirect />)} />
                  <Route path="trip-defaults" element={<Navigate to="/settings/general" replace />} />
                  <Route path=":section" element={withLoader(<LazySettingsContent />)} />
                </Route>
              </Route>

              {/* Workspace routes — WorkspaceChromeLayout supplies its own padding */}
              <Route path="/workspaces/:workspaceId" element={withWorkspaceLoader(<LazyWorkspaceChromeLayout />)}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={withWorkspaceLoader(<LazyWorkspaceOverviewPage />)} />
                <Route path="tasks" element={withWorkspaceLoader(<LazyWorkspaceTasksPage />)} />
                <Route path="documents" element={withWorkspaceLoader(<LazyWorkspaceDocumentsPage />)} />
                <Route path="timeline" element={withWorkspaceLoader(<LazyWorkspaceTimelinePage />)} />
                <Route path="resources" element={withWorkspaceLoader(<LazyWorkspaceResourcesPage />)} />
                <Route path="finances" element={withWorkspaceLoader(<LazyWorkspaceFinancesPage />)} />
                <Route path="settings" element={withWorkspaceLoader(<LazyWorkspaceSettingsPage />)} />
                {ENABLE_WORKSPACE_CHATBOT ? (
                  <Route
                    path="settings/chatbot"
                    element={withWorkspaceLoader(<LazyWorkspaceChatbotSettingsPage />)}
                  />
                ) : (
                  <Route
                    path="settings/chatbot"
                    element={<Navigate to=".." relative="path" replace />}
                  />
                )}
              </Route>
            </Route>
          </Route>
        </Route>

        <Route path="/" element={withLoader(<LazyLandingPage />)} />
        {/* Phase 2: legacy public marketing URL */}
        <Route path="/templates" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={withLoader(<LazyContactPage />)} />
        <Route path="/terms" element={withLoader(<LazyTermsPage />)} />
        <Route path="/privacy" element={withLoader(<LazyPrivacyPage />)} />
        <Route path="/cookies" element={withLoader(<LazyCookiesPage />)} />
        <Route path="/blog" element={withLoader(<LazyBlogListPage />)} />
        <Route path="/blog/:slug" element={withLoader(<LazyBlogPostPage />)} />
        <Route path="/book-demo" element={withLoader(<LazyBookDemoPage />)} />
        <Route path="/accept-invite" element={withLoader(<LazyAcceptInvitePage />)} />
        <Route path="/pricing" element={<Navigate to={{ pathname: "/", hash: "pricing" }} replace />} />
        <Route path="*" element={withLoader(<LazyNotFoundPage />)} />
      </Routes>
    </>
  )
}
